import { Injectable } from '@angular/core';
import { AccountInfo, AuthenticationResult, BrowserCacheLocation, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../../../../environments/environment';

const LOGIN_SCOPES = ['openid', 'profile', 'email'];
const REDIRECT_BRIDGE_PATH = 'auth/redirect.html';
const PENDING_SIGN_IN_KEY = 'incentivos_entra_sign_in_pending';

function createClient(): PublicClientApplication {
  const { tenantId, clientId } = environment.entraId;

  return new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: new URL(REDIRECT_BRIDGE_PATH, document.baseURI).href,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      loggerOptions: {
        logLevel: environment.production ? LogLevel.Error : LogLevel.Warning,
        piiLoggingEnabled: false,
        loggerCallback: (level, message) => (level === LogLevel.Error ? console.error(message) : console.warn(message)),
      },
    },
  });
}

@Injectable({ providedIn: 'root' })
export class EntraIdService {
  private readonly client = createClient();
  private readonly initialized = this.client.initialize();

  async signIn(): Promise<void> {
    await this.initialized;
    sessionStorage.setItem(PENDING_SIGN_IN_KEY, '1');
    await this.client.loginRedirect({ scopes: LOGIN_SCOPES, prompt: 'select_account' });
  }

  hasPendingSignIn(): boolean {
    return sessionStorage.getItem(PENDING_SIGN_IN_KEY) !== null;
  }

  /**
   * Known MSAL v5 issue: a logout + sign-in within a few seconds can intermittently fail with state_mismatch
   * (https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/8804, open with no fix).
   * MSAL resets its state on that error, so the next attempt works; the login form just shows the error.
   */
  async completeSignIn(): Promise<AuthenticationResult | null> {
    await this.initialized;
    try {
      return await this.client.handleRedirectPromise();
    } finally {
      this.clearPendingSignIn();
    }
  }

  clearPendingSignIn(): void {
    sessionStorage.removeItem(PENDING_SIGN_IN_KEY);
  }

  async clearSession(account: AccountInfo): Promise<void> {
    await this.initialized;
    await this.client.clearCache({ account });
  }
}
